/// <reference types="google.maps" />

import React, { useEffect, useState, useRef, useCallback } from 'react';
import { Property } from '../../types';
import { MapPin, Coffee, ShoppingBag, Utensils, Dumbbell, GraduationCap, Hospital, Store, Trees, Pill, AlertCircle, ExternalLink } from 'lucide-react';
import { LoadingSpinner } from '../ui/LoadingSpinner';
import { secureConfigService } from '../../services/secureConfigService';

interface PropertyMapWidgetProps {
  property: Property;
}

interface NearbyPlace {
  id: string;
  name: string;
  distance: string;
  type: string;
  location: google.maps.LatLng;
  icon: React.ReactNode;
}

const placeTypeIcons: Record<string, React.ReactNode> = {
  cafe: <Coffee className="w-4 h-4" />,
  restaurant: <Utensils className="w-4 h-4" />,
  supermarket: <ShoppingBag className="w-4 h-4" />,
  gym: <Dumbbell className="w-4 h-4" />,
  library: <GraduationCap className="w-4 h-4" />,
  hospital: <Hospital className="w-4 h-4" />,
  pharmacy: <Pill className="w-4 h-4" />,
  park: <Trees className="w-4 h-4" />,
  store: <Store className="w-4 h-4" />,
};

const PropertyMapWidget: React.FC<PropertyMapWidgetProps> = ({ property }) => {
  const mapRef = useRef<HTMLDivElement>(null);
  const [map, setMap] = useState<google.maps.Map | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [nearbyPlaces, setNearbyPlaces] = useState<NearbyPlace[]>([]);
  const [hoveredPlaceId, setHoveredPlaceId] = useState<string | null>(null);
  const [geocodedLocation, setGeocodedLocation] = useState<{lat: number, lng: number} | null>(null);
  const markersRef = useRef<Map<string, google.maps.Marker>>(new Map());
  const propertyMarkerRef = useRef<google.maps.Marker | null>(null);

  const calculateDistance = useCallback((lat1: number, lng1: number, lat2: number, lng2: number): string => {
    if (typeof google === 'undefined' || !google.maps?.geometry) {
      const R = 3959; // Earth's radius in miles
      const dLat = (lat2 - lat1) * Math.PI / 180;
      const dLng = (lng2 - lng1) * Math.PI / 180;
      const a = Math.sin(dLat/2) * Math.sin(dLat/2) +
                Math.cos(lat1 * Math.PI / 180) * Math.cos(lat2 * Math.PI / 180) *
                Math.sin(dLng/2) * Math.sin(dLng/2);
      const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1-a));
      const distance = R * c;
      return distance < 0.1 ? `${Math.round(distance * 5280)} ft` : `${distance.toFixed(1)} mi`;
    }
    
    const distance = google.maps.geometry.spherical.computeDistanceBetween(
      new google.maps.LatLng(lat1, lng1),
      new google.maps.LatLng(lat2, lng2)
    );
    const miles = distance * 0.000621371;
    return miles < 0.1 ? `${Math.round(miles * 5280)} ft` : `${miles.toFixed(1)} mi`;
  }, []);

  const handleAmenityHover = useCallback((placeId: string | null) => {
    setHoveredPlaceId(placeId);
    
    if (placeId) {
      const marker = markersRef.current.get(placeId);
      if (marker) {
        marker.setAnimation(google.maps.Animation.BOUNCE);
        marker.setIcon({
          path: google.maps.SymbolPath.CIRCLE,
          scale: 10,
          fillColor: '#ff3571',
          fillOpacity: 1,
          strokeColor: '#ffffff',
          strokeWeight: 2,
        });
        
        setTimeout(() => {
          if (marker.getAnimation() !== null) {
            marker.setAnimation(null);
          }
        }, 700);
      }
    } else {
      markersRef.current.forEach((marker) => {
        marker.setAnimation(null);
        marker.setIcon({
          path: google.maps.SymbolPath.CIRCLE,
          scale: 7,
          fillColor: '#8b5cf6',
          fillOpacity: 0.9,
          strokeColor: '#ffffff',
          strokeWeight: 2,
        });
      });
    }
  }, []);

  // Geocode address to get coordinates
  const geocodeAddress = useCallback(async (address: string): Promise<{lat: number, lng: number} | null> => {
    if (typeof google === 'undefined' || !google.maps?.Geocoder) {
      return null;
    }

    return new Promise((resolve) => {
      const geocoder = new google.maps.Geocoder();
      geocoder.geocode({ address }, (results, status) => {
        if (status === google.maps.GeocoderStatus.OK && results && results[0]) {
          const location = results[0].geometry.location;
          resolve({ lat: location.lat(), lng: location.lng() });
        } else {
          console.log('Geocoding failed for address:', address, 'Status:', status);
          resolve(null);
        }
      });
    });
  }, []);

  useEffect(() => {
    const initMap = async () => {
      try {
        await secureConfigService.initialize();
        const apiKey = secureConfigService.getConfig('googleMapsApiKey');

        if (!apiKey) {
          setError('Map service temporarily unavailable');
          setLoading(false);
          return;
        }

        // Load Google Maps script first
        if (!window.google?.maps) {
          const script = document.createElement('script');
          script.src = `https://maps.googleapis.com/maps/api/js?key=${apiKey}&libraries=places,geometry`;
          script.async = true;
          script.defer = true;
          
          await new Promise<void>((resolve, reject) => {
            script.onload = () => resolve();
            script.onerror = () => reject(new Error('Failed to load Google Maps'));
            document.head.appendChild(script);
          });
        }

        // Now determine coordinates - either from property or by geocoding
        let lat = property.latitude;
        let lng = property.longitude;

        // If no coordinates, try to geocode from address
        if (!lat || !lng) {
          const fullAddress = [
            property.address,
            property.city,
            property.state,
            property.zip_code
          ].filter(Boolean).join(', ');

          if (fullAddress) {
            console.log('Geocoding address:', fullAddress);
            const geocoded = await geocodeAddress(fullAddress);
            if (geocoded) {
              lat = geocoded.lat;
              lng = geocoded.lng;
              setGeocodedLocation(geocoded);
            }
          }
        }

        // Still no coordinates? Show error
        if (!lat || !lng) {
          setError(`Location data not available for this property`);
          setLoading(false);
          return;
        }

        if (!mapRef.current) return;

        const propertyLocation = new google.maps.LatLng(lat, lng);
        
        const mapInstance = new google.maps.Map(mapRef.current, {
          center: propertyLocation,
          zoom: 14,
          mapTypeControl: false,
          streetViewControl: false,
          fullscreenControl: false,
          zoomControl: true,
          styles: [
            { featureType: 'poi', elementType: 'labels', stylers: [{ visibility: 'off' }] }
          ],
        });

        // Add property marker with brand color
        const propertyMarker = new google.maps.Marker({
          position: propertyLocation,
          map: mapInstance,
          icon: {
            path: google.maps.SymbolPath.CIRCLE,
            scale: 12,
            fillColor: '#ff3571', // Brand color
            fillOpacity: 1,
            strokeColor: '#ffffff',
            strokeWeight: 3,
          },
          title: property.address,
        });

        propertyMarkerRef.current = propertyMarker;
        setMap(mapInstance);

        // Search for nearby places
        const service = new google.maps.places.PlacesService(mapInstance);
        const placeTypes = ['cafe', 'restaurant', 'supermarket', 'gym', 'library', 'hospital', 'pharmacy', 'park'];
        const allPlaces: NearbyPlace[] = [];

        const searchPromises = placeTypes.map((type) =>
          new Promise<void>((resolve) => {
            service.nearbySearch(
              {
                location: propertyLocation,
                radius: 1609, // 1 mile
                type: type,
              },
              (results, status) => {
                if (status === google.maps.places.PlacesServiceStatus.OK && results) {
                  results.slice(0, 2).forEach((place) => {
                    if (place.geometry?.location && place.place_id && place.name) {
                      const distance = calculateDistance(
                        lat!,
                        lng!,
                        place.geometry.location.lat(),
                        place.geometry.location.lng()
                      );

                      allPlaces.push({
                        id: place.place_id,
                        name: place.name,
                        distance,
                        type,
                        location: place.geometry.location,
                        icon: placeTypeIcons[type] || <Store className="w-4 h-4" />,
                      });

                      // Add marker for this place
                      const marker = new google.maps.Marker({
                        position: place.geometry.location,
                        map: mapInstance,
                        icon: {
                          path: google.maps.SymbolPath.CIRCLE,
                          scale: 7,
                          fillColor: '#8b5cf6',
                          fillOpacity: 0.9,
                          strokeColor: '#ffffff',
                          strokeWeight: 2,
                        },
                        title: place.name,
                      });

                      markersRef.current.set(place.place_id, marker);
                    }
                  });
                }
                resolve();
              }
            );
          })
        );

        await Promise.all(searchPromises);

        // Sort by distance and limit to 8 closest
        allPlaces.sort((a, b) => {
          const aNum = parseFloat(a.distance);
          const bNum = parseFloat(b.distance);
          return aNum - bNum;
        });

        setNearbyPlaces(allPlaces.slice(0, 8));
        setLoading(false);
      } catch (err) {
        console.error('Map initialization error:', err);
        setError('Unable to load map');
        setLoading(false);
      }
    };

    initMap();

    return () => {
      markersRef.current.forEach((marker) => marker.setMap(null));
      markersRef.current.clear();
      if (propertyMarkerRef.current) {
        propertyMarkerRef.current.setMap(null);
      }
    };
  }, [property, calculateDistance, geocodeAddress]);

  if (loading) {
    return (
      <div className="w-full space-y-4">
        <div className="w-full h-64 md:h-80 lg:h-96 rounded-xl border border-border/50 bg-muted/20 flex items-center justify-center">
          <div className="flex flex-col items-center gap-3">
            <LoadingSpinner size="lg" className="text-primary" />
            <p className="text-sm text-muted-foreground">Loading map...</p>
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    const fullAddress = [property.address, property.city, property.state, property.zip_code]
      .filter(Boolean)
      .join(', ');
    const mapsUrl = `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(fullAddress)}`;

    return (
      <div className="w-full space-y-4">
        <div className="w-full h-64 rounded-xl border border-border/50 bg-muted/20 flex items-center justify-center">
          <div className="flex flex-col items-center gap-4 p-6 text-center max-w-sm">
            <div className="p-4 bg-muted/50 rounded-full">
              <AlertCircle className="w-10 h-10 text-muted-foreground" />
            </div>
            <div>
              <p className="text-base font-medium text-foreground mb-1">Map Unavailable</p>
              <p className="text-sm text-muted-foreground mb-4">{error}</p>
            </div>
            {fullAddress && (
              <a 
                href={mapsUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-2 text-sm text-primary hover:underline font-medium"
              >
                <ExternalLink className="w-4 h-4" />
                View on Google Maps
              </a>
            )}
          </div>
        </div>
        <div className="px-4 md:px-6">
          <div className="flex items-center gap-2">
            <MapPin className="w-4 h-4 text-primary flex-shrink-0" />
            <p className="text-sm text-muted-foreground">
              {property.address ? `${property.address}, ` : ''}{property.city}, {property.state} {property.zip_code}
            </p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="w-full space-y-4">
      {/* Interactive Google Map */}
      <div className="w-full h-64 md:h-80 lg:h-96 rounded-xl border border-border/50 relative overflow-hidden shadow-lg">
        <div ref={mapRef} className="w-full h-full" />
        
        {/* Location Badge */}
        <div className="absolute top-4 left-4 bg-background/95 backdrop-blur-sm rounded-lg shadow-lg px-3 py-2 z-10 border border-border/50 max-w-[calc(100%-2rem)]">
          <div className="flex items-center gap-2">
            <MapPin className="w-4 h-4 text-primary flex-shrink-0" />
            <p className="text-xs font-medium text-foreground truncate">
              {property.address ? `${property.address}, ${property.city}, ${property.state}` : `${property.city}, ${property.state}`}
            </p>
          </div>
        </div>

        {/* Geocoded indicator */}
        {geocodedLocation && (
          <div className="absolute top-4 right-4 bg-primary/10 backdrop-blur-sm rounded-lg px-2 py-1 z-10 border border-primary/20">
            <p className="text-xs text-primary font-medium">📍 Location estimated</p>
          </div>
        )}
      </div>
      
      <div className="px-4 md:px-6 space-y-4">
        <div className="flex items-center gap-2">
          <MapPin className="w-4 h-4 text-primary flex-shrink-0" />
          <p className="text-sm text-muted-foreground">
            {property.address}, {property.city}, {property.state} {property.zip_code}
          </p>
        </div>

        {nearbyPlaces.length > 0 && (
          <div className="space-y-3">
            <h3 className="text-sm font-semibold text-foreground">Nearby Amenities</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
              {nearbyPlaces.map((place) => (
                <div
                  key={place.id}
                  onMouseEnter={() => handleAmenityHover(place.id)}
                  onMouseLeave={() => handleAmenityHover(null)}
                  className={`
                    flex items-center gap-3 p-3 rounded-xl transition-all duration-200 cursor-pointer
                    bg-muted/50 border border-border/50 hover:bg-muted hover:border-primary/30
                    ${hoveredPlaceId === place.id ? 'bg-muted border-primary/50 shadow-md scale-[1.02]' : ''}
                  `}
                >
                  <div className="flex-shrink-0 w-8 h-8 rounded-full bg-primary/10 flex items-center justify-center text-primary">
                    {place.icon}
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-foreground truncate">{place.name}</p>
                    <p className="text-xs text-muted-foreground">{place.distance} away</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {nearbyPlaces.length === 0 && !loading && (
          <div className="text-center py-6">
            <p className="text-sm text-muted-foreground">No nearby amenities found within 1 mile</p>
          </div>
        )}
      </div>
    </div>
  );
};

export default PropertyMapWidget;
